import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Container,
  Grid,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { Alert, Button } from "@platform/design-system";
import { RFP_ACTION_VALUES, RFP_FORM } from "@platform/rfp-core";

import ConfirmDialog from "../../../components/confirmDialog";
import FullPageError from "../../../components/fullPageError";
import { usePrompt } from "../../../hooks";
import { ROUTE_NAMES } from "../../../constants";
import { RFP_SCROLL_CONTAINER_ID, scrollToTop } from "../../../utils";
import { RfpWarning } from "../components";
import RfpError from "../components/rfpError";
import { RFP_ACTION_TYPES, RFP_ORIGIN_FLOW } from "../constants";
import { RfpProvider, useRfpContext } from "../contexts";
import CoverageDetails from "../coverages/coverageDetails";
import CustomStepper from "../customStepper";
import { validationByStep } from "../reducers/validations";

import CarrierSelection from "./carrierSelection";
import CoverageSelection from "./coverageSelection";
import Documents from "./documents";
import RfpForm from "./rfpForm";
import SummaryReview from "./SummaryReview";

const { CLIENT_ID, NAME } = RFP_FORM;
const { UPDATE_BACK_STEP, UPDATE_NEXT_STEP, UPDATE_STILL_PENDING_DOC } =
  RFP_ACTION_TYPES;
const {
  ACTIVE_STEP,
  SELECTED_COVERAGES,
  SELECTED_CARRIERS,
  ARE_DOCUMENTS_VALID,
  IS_CENTRO_CARRIER_SELECTED,
} = RFP_ACTION_VALUES;

interface StepProps {
  px: number;
}

interface StepConfig {
  name: string;
  component: (props: StepProps) => React.JSX.Element;
}

interface RfpLocationState {
  prevPath?: string;
}

const steps: StepConfig[] = [
  {
    name: "RfpForm",
    component: (p) => <RfpForm {...p} />,
  },
  {
    name: "Coverages",
    component: (p) => <CoverageSelection {...p} />,
  },
  {
    name: "Coverage",
    component: (p) => <CoverageDetails {...p} />,
  },
  {
    name: "Carriers",
    component: (p) => <CarrierSelection {...p} />,
  },
  {
    name: "Documents",
    component: (p) => <Documents {...p} />,
  },
  {
    name: "Summary",
    component: (p) => <SummaryReview {...p} />,
  },
];

const propsByStep: StepProps[] = [
  { px: 20 },
  { px: 20 },
  { px: 3 },
  { px: 18 },
  { px: 13 },
  { px: 12 },
];

const RFP = (): React.JSX.Element => (
  <RfpProvider>
    <RFPContainer />
  </RfpProvider>
);

const errorMessages: Record<string, string> = {
  [RFP_ORIGIN_FLOW.CLIENT]: "We are having trouble starting a new RFP",
  [RFP_ORIGIN_FLOW.DRAFT]: "We are having trouble loading your RFP",
};

const RFPContainer = (): React.JSX.Element => {
  const {
    form,
    rfp,
    setRfp,
    enableWarningNavigation,
    isDiscardDialogOpen,
    handleDiscardRfpDraft,
    setIsDiscardDialogOpen,
    fetchingRfpError,
    discardDraftError,
    originRfp,
    dispatchCall,
    submittingError,
    handleCloseSubmitError,
    handleSaveDraft,
    isSavingDraftRfp,
  } = useRfpContext();

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const justOpen = useRef(true);
  const locationState = (location.state as RfpLocationState | null) ?? null;
  const prevPath = locationState?.prevPath ?? null;
  const activeStep = (rfp[ACTIVE_STEP] as number | undefined) ?? 0;
  const pathsCurrClient = [
    `/${ROUTE_NAMES.CLIENT_SELECTION}`,
    `/${ROUTE_NAMES.CLIENTS}/${form[CLIENT_ID]}`,
  ];

  const [showPrompt, confirmNavigation, cancelNavigation] = usePrompt(
    enableWarningNavigation,
    location,
    { client: form },
    [`/${ROUTE_NAMES.CLIENT_SELECTION}`],
    prevPath,
  ) as [boolean, () => void, () => void];

  const saveDraftRef = useRef(handleSaveDraft);
  saveDraftRef.current = handleSaveDraft;

  useEffect((): void => {
    if (justOpen.current) {
      justOpen.current = false;
    } else {
      void saveDraftRef.current();
    }
  }, [activeStep]);

  const handleCloseWarning = (isSaveDraft: boolean): void => {
    if (isSaveDraft) {
      void handleSaveDraft(true);
      setRfp({ type: UPDATE_STILL_PENDING_DOC, value: false });
      confirmNavigation();
    } else {
      cancelNavigation();
    }
  };

  function handleDiscardClose(): void {
    setIsDiscardDialogOpen(false);
  }

  const getBackBtnProps = (): {
    text: string;
    variant: "text" | "outlined";
    icon: boolean;
  } => {
    let text = "Back";
    let variant: "text" | "outlined" = "text";
    let icon = true;
    if (activeStep === 0 && prevPath === pathsCurrClient[1]) {
      text = "Cancel";
      variant = "outlined";
      icon = false;
    }
    return { text, variant, icon };
  };

  const handleBackNavigation = (): void => {
    const nextPath = locationState?.prevPath ?? ROUTE_NAMES.RFPS;
    navigate(nextPath);
  };

  const renderBtnActions = ({ px }: StepProps): React.JSX.Element => {
    const handleOnClick = (type: { type: string }): void => {
      setRfp(type);
      scrollToTop();
    };

    return (
      <Grid
        container
        size={12}
        justifyContent="space-between"
        mt={3}
        maxHeight={theme.spacing(4.5)}
        px={px}
      >
        {/* TODO(ds-button): needs manual review — variant is computed at runtime, no single DS variant maps to it */}
        <MuiButton
          color={"brandPrimary"}
          variant={getBackBtnProps().variant}
          startIcon={getBackBtnProps().icon ? <ArrowBack /> : null}
          disabled={isSavingDraftRfp}
          onClick={() => handleOnClick({ type: UPDATE_BACK_STEP })}
        >
          {getBackBtnProps().text}
        </MuiButton>
        <Button
          data-testid="rfp-next-step"
          variant="primary"
          onClick={() => handleOnClick({ type: UPDATE_NEXT_STEP })}
          disabled={
            validationByStep(
              rfp[SELECTED_COVERAGES],
              rfp[SELECTED_CARRIERS],
              rfp[ARE_DOCUMENTS_VALID],
              rfp[IS_CENTRO_CARRIER_SELECTED],
            )[activeStep] || isSavingDraftRfp
          }
        >
          NEXT: {steps[activeStep + 1]?.name}
        </Button>
      </Grid>
    );
  };

  const pagesToSkipBtns = [0, 2, 4, steps.length - 1];

  const renderBody = (): React.JSX.Element | null => {
    if (discardDraftError) {
      return (
        <Box mt={2} mb={2}>
          <Alert severity="error">{discardDraftError}</Alert>
        </Box>
      );
    }

    if (steps[activeStep]) {
      return (
        <Grid container>
          {steps[activeStep].component(propsByStep[activeStep])}
          {!pagesToSkipBtns.includes(activeStep) &&
            renderBtnActions(propsByStep[activeStep])}
        </Grid>
      );
    }
    handleBackNavigation();
    return null;
  };

  const renderBodyWrapper = (): React.JSX.Element => (
    <Box id={RFP_SCROLL_CONTAINER_ID} mb={5} mt={8} sx={{ overflowY: "auto" }}>
      <CustomStepper />
      <Container sx={{ mt: 24 }}>{renderBody()}</Container>
    </Box>
  );

  if (submittingError) {
    return <RfpError handleAction={handleCloseSubmitError} />;
  }

  return (
    <>
      <RfpWarning
        open={showPrompt}
        onClose={handleCloseWarning}
        isSavingDraftRfp={isSavingDraftRfp}
        isBlockByPendingDoc={false}
      />
      {isDiscardDialogOpen ? (
        <ConfirmDialog
          isOpen={isDiscardDialogOpen}
          title={`Discard RFP Draft`}
          message={`Are you sure you want to discard this draft for ${form[NAME]}?`}
          confirmBtnMsg="YES, DISCARD"
          onClose={handleDiscardClose}
          onConfirm={() => {
            void handleDiscardRfpDraft();
            handleDiscardClose();
          }}
        />
      ) : null}
      {fetchingRfpError ? (
        <FullPageError
          error={errorMessages[originRfp ?? ""] ?? ""}
          onClick={() => dispatchCall()}
        />
      ) : (
        renderBodyWrapper()
      )}
    </>
  );
};

export default RFP;
